// src/utils/axiosConfig.js
/* global globalThis */
import axios from 'axios';
import { getSelectedCicloEscolarId, getSelectedSchoolId } from './schoolContext';

const API_URL = 'https://api.transportesluvan.com/api';


const api = axios.create({
    baseURL: API_URL,
});

const WRITE_METHODS = new Set(['post', 'put', 'patch']);

// Roles that have a fixed school context and require automatic school injection (parent=3, colaborador=8)
const SCHOOL_CONTEXT_ROLE_IDS = new Set([3, 8]);

const getTokenRoleId = (token) => {
    if (!token) return null;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload?.roleId ?? null;
    } catch {
        return null;
    }
};

// Cuando el usuario está en una página cuyo ciclo escolar viene fijado en la ruta
// (/admin/escuelas/ciclo/:cicloEscolarId/*), ese ciclo es la fuente autoritativa
// para todas sus llamadas: gana sobre el ciclo "activo" global del localStorage.
// Evita que, p. ej. al abrir una boleta de un colegio de otro ciclo desde una
// notificación, las peticiones queden filtradas por el ciclo que el admin tenía
// seleccionado. Auto-revierte al salir de la ruta; un cicloEscolarId explícito en
// params/body/url sigue ganando vía hasExplicitCycleOverride.
const CICLO_ROUTE_RE = /\/escuelas\/ciclo\/(\d+)(?:\/|$)/;

const getRouteCicloEscolarId = () => {
    try {
        const match = CICLO_ROUTE_RE.exec(globalThis.location?.pathname || '');
        return match ? match[1] : null;
    } catch {
        return null;
    }
};

// Solo un id de ciclo entero positivo es válido para inyectar. Valores como
// 'all' (filtro "todos los ciclos"), '' o 'undefined' harían que el backend
// descarte el filtro de ciclo (normalizeInteger('all') === null) y lea/escriba
// datos fuera del ciclo esperado.
const normalizeCycleId = (raw) => {
    const parsed = Number.parseInt(raw, 10);
    return Number.isInteger(parsed) && parsed > 0 ? String(parsed) : null;
};

const getCycleContext = () => {
    const token = localStorage.getItem('token');
    const routeCicloEscolarId = getRouteCicloEscolarId();
    return {
        token,
        roleId: getTokenRoleId(token),
        selectedCicloEscolarId: routeCicloEscolarId || normalizeCycleId(getSelectedCicloEscolarId()),
        // True only when the cycle came from the /escuelas/ciclo/:id/* route (deliberate archived-
        // cycle view), never for the tab's generic "last selected cycle". The backend uses this to
        // decide whether an explicit cicloEscolarId is allowed to override the school's own cycle.
        isRouteOverride: !!routeCicloEscolarId,
        selectedSchoolId: getSelectedSchoolId()
    };
};

const shouldInjectCycleContext = (url = '') => {
    const skippedFragments = [
        '/school-years',
        '/ciclos-escolares',
        '/protocols',
        '/update-parent-info',
        '/update-colaborador-info'
    ];
    return !skippedFragments.some((fragment) => url.includes(fragment));
};

const ensureObjectParams = (config) => {
    if (!config.params || typeof config.params !== 'object') {
        config.params = {};
    }
    return config.params;
};

const isJsonBody = (data) => {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return data && typeof data === 'object' && !isFormData;
};

const CYCLE_OVERRIDE_KEYS = new Set(['allCycles', 'cicloEscolarId', 'ciclo_escolar_id']);
const SCHOOL_OVERRIDE_KEYS = new Set(['schoolId', 'school_id']);

const hasOverrideInParams = (params, overrideKeys) => {
    if (!params || typeof params !== 'object') return false;
    if (params instanceof URLSearchParams) {
        return Array.from(overrideKeys).some((key) => params.has(key));
    }
    const keys = Object.keys(params);
    return keys.some((key) => overrideKeys.has(key));
};

const hasOverrideInUrl = (url = '', overrideKeys) => {
    const queryIndex = String(url).indexOf('?');
    if (queryIndex === -1) return false;

    const searchParams = new URLSearchParams(String(url).slice(queryIndex + 1));
    return Array.from(overrideKeys).some((key) => searchParams.has(key));
};

const hasExplicitCycleOverride = (config) => {
    if (hasOverrideInUrl(config.url || '', CYCLE_OVERRIDE_KEYS)) return true;
    if (hasOverrideInParams(config.params, CYCLE_OVERRIDE_KEYS)) return true;
    if (isJsonBody(config.data) && hasOverrideInParams(config.data, CYCLE_OVERRIDE_KEYS)) return true;
    return false;
};

const hasExplicitSchoolOverride = (config) => {
    if (hasOverrideInUrl(config.url || '', SCHOOL_OVERRIDE_KEYS)) return true;
    if (hasOverrideInParams(config.params, SCHOOL_OVERRIDE_KEYS)) return true;
    if (isJsonBody(config.data) && hasOverrideInParams(config.data, SCHOOL_OVERRIDE_KEYS)) return true;
    return false;
};

const injectCycleHeaders = (config, cycleContext) => {
    if (cycleContext.selectedCicloEscolarId) {
        config.headers['X-Ciclo-Escolar-Id'] = cycleContext.selectedCicloEscolarId;
        if (cycleContext.isRouteOverride) {
            config.headers['X-Ciclo-Escolar-Route-Override'] = '1';
        }
    }
};

const injectSchoolHeaders = (config, cycleContext) => {
    if (cycleContext.selectedSchoolId) {
        config.headers['X-School-Id'] = cycleContext.selectedSchoolId;
    }
};

const injectCycleParams = (config, cycleContext) => {
    const params = ensureObjectParams(config);
    if (cycleContext.selectedCicloEscolarId && !('cicloEscolarId' in params)) {
        params.cicloEscolarId = cycleContext.selectedCicloEscolarId;
    }
};

const injectSchoolParams = (config, cycleContext) => {
    const params = ensureObjectParams(config);
    if (cycleContext.selectedSchoolId && !('schoolId' in params)) {
        params.schoolId = cycleContext.selectedSchoolId;
    }
};

const injectCycleBody = (config, cycleContext) => {
    if (!isJsonBody(config.data)) return;
    if (cycleContext.selectedCicloEscolarId && !('cicloEscolarId' in config.data)) {
        config.data.cicloEscolarId = cycleContext.selectedCicloEscolarId;
    }
};

const injectSchoolBody = (config, cycleContext) => {
    if (!isJsonBody(config.data)) return;
    if (cycleContext.selectedSchoolId && !('schoolId' in config.data)) {
        config.data.schoolId = cycleContext.selectedSchoolId;
    }
};

const applyRequestConfig = (config) => {
    const cycleContext = getCycleContext();
    const method = (config.method || 'get').toLowerCase();
    const url = (config.url || '').toString();
    config.headers = config.headers || {};

    if (!config.skipAuth && cycleContext.token) {
        config.headers.Authorization = `Bearer ${cycleContext.token}`;
    }

    if (config.skipSchoolCycleContext) return config;

    if (!shouldInjectCycleContext(url)) return config;

    if (!hasExplicitCycleOverride(config)) {
        injectCycleHeaders(config, cycleContext);
        if (method === 'get') {
            injectCycleParams(config, cycleContext);
        }
        if (WRITE_METHODS.has(method)) {
            injectCycleBody(config, cycleContext);
        }
    }

    // Only auto-inject school context for roles that have a fixed school (parent/colaborador).
    // Admin users manage their own schoolId filtering explicitly per request.
    if (!hasExplicitSchoolOverride(config) && SCHOOL_CONTEXT_ROLE_IDS.has(Number(cycleContext.roleId))) {
        injectSchoolHeaders(config, cycleContext);
        if (method === 'get') {
            injectSchoolParams(config, cycleContext);
        }
        if (WRITE_METHODS.has(method)) {
            injectSchoolBody(config, cycleContext);
        }
    }
    return config;
};

api.interceptors.request.use(
    applyRequestConfig,
    (error) => { throw error; }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) prom.reject(error);
        else prom.resolve(token);
    });
    failedQueue = [];
};

const clearStoredSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
};

const isSessionInvalidatedMessage = (message) => (
    message.includes('Sesión inválida') ||
    message.includes('Session inválida') ||
    message.includes('inicia sesión nuevamente')
);

const dispatchSessionInvalidated = (message) => {
    clearStoredSession();
    globalThis.dispatchEvent(new CustomEvent('sessionInvalidated', {
        detail: { message }
    }));
};

const waitForRefresh = (originalRequest) => (
    new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
    }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
    })
);

const refreshAuthToken = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    const response = await axios.post(`${api.defaults.baseURL.replace('/api', '')}/api/auth/refresh`, { refreshToken });
    const { token, refreshToken: newRefresh } = response.data;
    localStorage.setItem('token', token);
    if (newRefresh) {
        localStorage.setItem('refreshToken', newRefresh);
    }
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    return token;
};

const handleUnauthorizedResponse = async (error) => {
    const originalRequest = error.config;
    const errorMessage = error.response?.data?.message || '';

    if (!originalRequest) throw error;

    // Un 401 del login son credenciales incorrectas, no un token vencido: no refrescar
    if (originalRequest.url === '/auth/login') throw error;

    if (isSessionInvalidatedMessage(errorMessage)) {
        dispatchSessionInvalidated(errorMessage);
        throw error;
    }

    if (originalRequest._retry) throw error;
    originalRequest._retry = true;

    if (isRefreshing) {
        return waitForRefresh(originalRequest);
    }

    isRefreshing = true;
    try {
        const token = await refreshAuthToken();
        if (!token) throw error;
        processQueue(null, token);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
    } catch (refreshError) {
        processQueue(refreshError, null);
        clearStoredSession();
        throw refreshError;
    } finally {
        isRefreshing = false;
    }
};

const FORCE_PASSWORD_CHANGE_PATH = '/force-password-change';

const redirectToForcePasswordChange = () => {
    if (globalThis.location.pathname !== FORCE_PASSWORD_CHANGE_PATH) {
        globalThis.location.assign(FORCE_PASSWORD_CHANGE_PATH);
    }
};

api.interceptors.response.use(
    response => response,
    async (error) => {
        if (error.response?.status === 401) {
            return handleUnauthorizedResponse(error);
        }
        if (error.response?.status === 403 && error.response.data?.code === 'PASSWORD_CHANGE_REQUIRED') {
            redirectToForcePasswordChange();
        }
        throw error;
    }
);

export default api;
